import { useState, useEffect, useMemo } from 'react';
import {
  AdminServiceStatus,
  AdminError,
  AdminPerformanceMetrics,
  AdminCacheStats,
  AdminDatabaseHealth,
  ServiceUptimeDataPoint,
  ResponseTimeDataPoint,
  ServiceHealthHeatmapData,
  ErrorRateDataPoint,
  ErrorDistribution,
  RequestRateDataPoint,
  QueryDurationDataPoint,
  GeographicLatency,
  CacheHitRateDataPoint,
  StorageGrowthDataPoint,
  CacheOperationsDataPoint,
  TopCachedResource,
  DatabaseGrowthDataPoint,
  SlowQuery,
  TableSize,
  SystemHealthData,
} from '../../types/admin';
import { subDays, subHours, format } from 'date-fns';

/**
 * useAdminSystem - Hook for system health monitoring
 *
 * Generates realistic mock data for:
 * - Service status monitoring (5 Cloudflare services)
 * - Error tracking and analysis
 * - Performance metrics (API, Worker, Database)
 * - Cache management (R2, KV)
 * - Database health monitoring
 *
 * All data is consistent and tells a story of a healthy platform
 */

// Helper function to generate random value with variance
function randomWithVariance(base: number, variance: number): number {
  return Math.round(base + (Math.random() - 0.5) * 2 * variance);
}

// Generate service status data
function generateServiceStatusData(): AdminServiceStatus[] {
  const services = [
    {
      service: 'Workers API',
      description: 'Cloudflare Workers handling API requests',
      baseUptime: 99.95,
      baseResponseTime: 45,
      baseErrorRate: 0.15,
      baseRequestRate: 1250,
    },
    {
      service: 'Durable Objects Chat',
      description: 'WebSocket chat via Durable Objects',
      baseUptime: 99.92,
      baseResponseTime: 28,
      baseErrorRate: 0.22,
      baseRequestRate: 380,
    },
    {
      service: 'D1 Database',
      description: 'SQLite-based database on Cloudflare',
      baseUptime: 99.98,
      baseResponseTime: 12,
      baseErrorRate: 0.08,
      baseRequestRate: 2100,
    },
    {
      service: 'R2 Storage',
      description: 'Object storage for images and attachments',
      baseUptime: 99.99,
      baseResponseTime: 35,
      baseErrorRate: 0.05,
      baseRequestRate: 650,
    },
    {
      service: 'Workers KV',
      description: 'Key-value cache storage',
      baseUptime: 99.97,
      baseResponseTime: 8,
      baseErrorRate: 0.12,
      baseRequestRate: 890,
    },
  ];

  return services.map((svc) => {
    const uptime24h = Math.min(100, randomWithVariance(svc.baseUptime, 0.05));
    const uptime7d = Math.min(100, randomWithVariance(svc.baseUptime, 0.1));
    const uptime30d = Math.min(100, randomWithVariance(svc.baseUptime, 0.15));

    // Determine status based on uptime
    let status: 'operational' | 'degraded' | 'down' = 'operational';
    if (uptime24h < 95) {
      status = 'down';
    } else if (uptime24h < 99.5) {
      status = 'degraded';
    }

    const p50 = randomWithVariance(svc.baseResponseTime, svc.baseResponseTime * 0.2);
    const p95 = randomWithVariance(svc.baseResponseTime * 1.8, svc.baseResponseTime * 0.3);
    const p99 = randomWithVariance(svc.baseResponseTime * 2.5, svc.baseResponseTime * 0.4);

    return {
      service: svc.service,
      description: svc.description,
      status,
      uptime: uptime24h,
      uptime_24h: uptime24h,
      uptime_7d: uptime7d,
      uptime_30d: uptime30d,
      response_time: p95, // Display p95 as main metric
      response_time_p50: p50,
      response_time_p95: p95,
      response_time_p99: p99,
      error_rate: randomWithVariance(svc.baseErrorRate, svc.baseErrorRate * 0.3),
      request_rate: randomWithVariance(svc.baseRequestRate, svc.baseRequestRate * 0.15),
      last_incident: status !== 'operational' ? 'High error rate detected' : undefined,
      last_incident_date:
        status !== 'operational' ? format(subHours(new Date(), 2), 'yyyy-MM-dd HH:mm:ss') : undefined,
    };
  });
}

// Generate uptime history (24 hours)
function generateUptimeHistory(services: AdminServiceStatus[]): ServiceUptimeDataPoint[] {
  const data: ServiceUptimeDataPoint[] = [];
  const now = new Date();

  services.forEach((service) => {
    for (let i = 23; i >= 0; i--) {
      const timestamp = format(subHours(now, i), 'yyyy-MM-dd HH:mm:ss');
      const uptime = Math.min(100, randomWithVariance(service.uptime_24h, 0.5));
      data.push({ timestamp, uptime, service: service.service });
    }
  });

  return data;
}

// Generate service health heatmap (7 days × 24 hours)
function generateHealthHeatmap(services: AdminServiceStatus[]): ServiceHealthHeatmapData[] {
  const data: ServiceHealthHeatmapData[] = [];

  services.forEach((service) => {
    for (let day = 0; day < 7; day++) {
      for (let hour = 0; hour < 24; hour++) {
        // Business hours (9am-5pm) have higher uptime
        const isBusinessHours = hour >= 9 && hour <= 17;
        const baseUptime = isBusinessHours ? service.uptime_7d : service.uptime_7d * 0.98;
        const uptime = Math.min(100, randomWithVariance(baseUptime, 0.5));

        data.push({
          hour,
          day,
          uptime,
          service: service.service,
        });
      }
    }
  });

  return data;
}

// Generate error data
function generateErrorData(): AdminError[] {
  const errorTypes = [
    'NetworkError',
    'ValidationError',
    'DatabaseError',
    'AuthenticationError',
    'WorkerError',
    'TimeoutError',
  ];
  const services = ['Workers API', 'Durable Objects Chat', 'D1 Database', 'R2 Storage', 'Workers KV'];
  const severities: ('critical' | 'error' | 'warning')[] = ['critical', 'error', 'warning'];

  const errors: AdminError[] = [];
  const errorCount = 35; // 35 recent errors

  for (let i = 0; i < errorCount; i++) {
    const errorType = errorTypes[Math.floor(Math.random() * errorTypes.length)];
    const service = services[Math.floor(Math.random() * services.length)];
    const severity = severities[Math.floor(Math.random() * severities.length)];
    const occurrenceCount = randomWithVariance(severity === 'critical' ? 50 : 15, 20);
    const firstOccurred = subHours(new Date(), randomWithVariance(72, 50));
    const lastOccurred = subHours(new Date(), randomWithVariance(2, 10));

    const messages = {
      NetworkError: 'Failed to connect to upstream service',
      ValidationError: 'Invalid request parameter: property_id',
      DatabaseError: 'Query timeout after 30s',
      AuthenticationError: 'JWT token expired',
      WorkerError: 'Worker exceeded CPU time limit',
      TimeoutError: 'Request timeout after 60s',
    };

    errors.push({
      id: `error_${i + 1}`,
      error_type: errorType,
      severity,
      service,
      endpoint: `/api/${service.toLowerCase().replace(/\s+/g, '-')}/endpoint`,
      message: messages[errorType as keyof typeof messages],
      stack_trace:
        severity === 'critical'
          ? 'Error: ' + messages[errorType as keyof typeof messages] + '\n  at handler (worker.js:42:15)'
          : undefined,
      user_id: Math.random() > 0.7 ? `user_${randomWithVariance(1000, 500)}` : undefined,
      occurrence_count: occurrenceCount,
      first_occurred_at: format(firstOccurred, 'yyyy-MM-dd HH:mm:ss'),
      last_occurred_at: format(lastOccurred, 'yyyy-MM-dd HH:mm:ss'),
      resolved: Math.random() > 0.3,
      resolved_at:
        Math.random() > 0.3 ? format(subHours(new Date(), randomWithVariance(1, 5)), 'yyyy-MM-dd HH:mm:ss') : undefined,
      resolved_by: Math.random() > 0.3 ? 'admin@cribcritic.ai' : undefined,
    });
  }

  return errors.sort((a, b) => b.last_occurred_at.localeCompare(a.last_occurred_at));
}

// Generate error rate history (24 hours)
function generateErrorRateHistory(): ErrorRateDataPoint[] {
  const data: ErrorRateDataPoint[] = [];
  const now = new Date();
  let baseErrorRate = 5; // errors per hour

  for (let i = 23; i >= 0; i--) {
    const timestamp = format(subHours(now, i), 'yyyy-MM-dd HH:mm:ss');
    // Simulate error spike 6 hours ago
    const spike = i === 6 ? 3 : 1;
    const errorCount = randomWithVariance(baseErrorRate * spike, 3);
    const errorRate = errorCount;

    data.push({ timestamp, error_count: errorCount, error_rate: errorRate });
  }

  return data;
}

// Generate error distribution
function generateErrorDistribution(errors: AdminError[]): ErrorDistribution[] {
  const typeCounts: { [key: string]: number } = {};
  errors.forEach((error) => {
    typeCounts[error.error_type] = (typeCounts[error.error_type] || 0) + error.occurrence_count;
  });

  const total = Object.values(typeCounts).reduce((sum, count) => sum + count, 0);

  return Object.entries(typeCounts)
    .map(([type, count]) => ({
      type,
      count,
      percentage: parseFloat(((count / total) * 100).toFixed(1)),
    }))
    .sort((a, b) => b.count - a.count);
}

// Generate performance metrics
function generatePerformanceMetrics(): AdminPerformanceMetrics {
  return {
    api_p50: randomWithVariance(42, 10),
    api_p95: randomWithVariance(125, 20),
    api_p99: randomWithVariance(285, 40),
    request_rate: randomWithVariance(5270, 500), // Total requests per minute
    throughput: parseFloat(randomWithVariance(1.8, 0.3).toFixed(2)), // MB/s
    worker_cpu_time: randomWithVariance(8, 2),
    worker_duration: randomWithVariance(45, 10),
    cold_start_rate: parseFloat(randomWithVariance(2.5, 0.5).toFixed(1)),
    db_query_time_avg: randomWithVariance(12, 3),
    db_query_time_p95: randomWithVariance(45, 10),
    db_query_time_p99: randomWithVariance(125, 25),
    db_queries_per_sec: randomWithVariance(35, 8),
    worker_memory: randomWithVariance(28, 5),
  };
}

// Generate response time history (24 hours)
function generateResponseTimeHistory(): ResponseTimeDataPoint[] {
  const data: ResponseTimeDataPoint[] = [];
  const now = new Date();

  for (let i = 23; i >= 0; i--) {
    const timestamp = format(subHours(now, i), 'yyyy-MM-dd HH:mm:ss');
    const p50 = randomWithVariance(42, 8);
    const p95 = randomWithVariance(125, 20);
    const p99 = randomWithVariance(285, 40);

    data.push({ timestamp, p50, p95, p99 });
  }

  return data;
}

// Generate request rate history (24 hours)
function generateRequestRateHistory(): RequestRateDataPoint[] {
  const data: RequestRateDataPoint[] = [];
  const now = new Date();

  for (let i = 23; i >= 0; i--) {
    const timestamp = format(subHours(now, i), 'yyyy-MM-dd HH:mm:ss');
    const hour = subHours(now, i).getHours();
    // Traffic patterns: higher during business hours
    const isBusinessHours = hour >= 9 && hour <= 17;
    const baseRequests = isBusinessHours ? 5500 : 2800;
    const requests = randomWithVariance(baseRequests, baseRequests * 0.15);

    data.push({ timestamp, requests });
  }

  return data;
}

// Generate query duration distribution
function generateQueryDurationDistribution(): QueryDurationDataPoint[] {
  return [
    { duration_bucket: '0-10ms', count: randomWithVariance(15000, 2000) },
    { duration_bucket: '10-50ms', count: randomWithVariance(8500, 1000) },
    { duration_bucket: '50-100ms', count: randomWithVariance(3200, 500) },
    { duration_bucket: '100-500ms', count: randomWithVariance(850, 200) },
    { duration_bucket: '500-1000ms', count: randomWithVariance(120, 30) },
    { duration_bucket: '1000ms+', count: randomWithVariance(25, 10) },
  ];
}

// Generate geographic latency data
function generateGeographicLatency(): GeographicLatency[] {
  const regions = [
    { region: 'North America (US East)', latitude: 40.7128, longitude: -74.006 },
    { region: 'North America (US West)', latitude: 37.7749, longitude: -122.4194 },
    { region: 'Europe (London)', latitude: 51.5074, longitude: -0.1278 },
    { region: 'Europe (Frankfurt)', latitude: 50.1109, longitude: 8.6821 },
    { region: 'Asia (Tokyo)', latitude: 35.6762, longitude: 139.6503 },
    { region: 'Asia (Singapore)', latitude: 1.3521, longitude: 103.8198 },
    { region: 'South America (São Paulo)', latitude: -23.5505, longitude: -46.6333 },
    { region: 'Australia (Sydney)', latitude: -33.8688, longitude: 151.2093 },
  ];

  return regions.map((r) => ({
    ...r,
    latency_p50: randomWithVariance(45, 15),
    latency_p95: randomWithVariance(125, 30),
    request_count: randomWithVariance(25000, 5000),
  }));
}

// Generate cache statistics
function generateCacheStats(): AdminCacheStats {
  return {
    r2: {
      total_objects: randomWithVariance(125000, 5000),
      total_size_gb: parseFloat(randomWithVariance(450, 50).toFixed(2)),
      bandwidth_in_gb: parseFloat(randomWithVariance(12, 3).toFixed(2)),
      bandwidth_out_gb: parseFloat(randomWithVariance(85, 15).toFixed(2)),
      operations_per_min: randomWithVariance(650, 100),
      get_operations: randomWithVariance(550, 80),
      put_operations: randomWithVariance(85, 20),
      delete_operations: randomWithVariance(15, 5),
      hit_rate: parseFloat(randomWithVariance(87, 3).toFixed(1)),
    },
    kv: {
      total_keys: randomWithVariance(8500, 500),
      read_operations: randomWithVariance(890, 150),
      write_operations: randomWithVariance(125, 30),
      hit_rate: parseFloat(randomWithVariance(92, 2).toFixed(1)),
      eviction_rate: parseFloat(randomWithVariance(1.2, 0.3).toFixed(1)),
      avg_value_size_kb: parseFloat(randomWithVariance(3.5, 0.8).toFixed(1)),
    },
    overall_hit_rate: parseFloat(randomWithVariance(89, 2).toFixed(1)),
    cache_size: randomWithVariance(1250, 100),
    eviction_rate: parseFloat(randomWithVariance(1.5, 0.3).toFixed(1)),
    bytes_saved: randomWithVariance(3500000000, 500000000), // ~3.5GB
    time_saved_ms: randomWithVariance(125000, 25000),
  };
}

// Generate cache hit rate history (24 hours)
function generateCacheHitRateHistory(): CacheHitRateDataPoint[] {
  const data: CacheHitRateDataPoint[] = [];
  const now = new Date();

  for (let i = 23; i >= 0; i--) {
    const timestamp = format(subHours(now, i), 'yyyy-MM-dd HH:mm:ss');
    const hit_rate = parseFloat(randomWithVariance(89, 2).toFixed(1));
    const r2_hit_rate = parseFloat(randomWithVariance(87, 3).toFixed(1));
    const kv_hit_rate = parseFloat(randomWithVariance(92, 2).toFixed(1));

    data.push({ timestamp, hit_rate, r2_hit_rate, kv_hit_rate });
  }

  return data;
}

// Generate storage growth history (30 days)
function generateStorageGrowthHistory(): StorageGrowthDataPoint[] {
  const data: StorageGrowthDataPoint[] = [];
  const now = new Date();
  let currentSize = 380; // GB
  let currentObjects = 100000;

  for (let i = 30; i >= 0; i--) {
    const date = format(subDays(now, i), 'yyyy-MM-dd');
    data.push({
      date,
      size_gb: parseFloat(currentSize.toFixed(2)),
      object_count: currentObjects,
    });

    // Growth per day
    currentSize += randomWithVariance(2.3, 0.5);
    currentObjects += randomWithVariance(850, 150);
  }

  return data;
}

// Generate cache operations history (24 hours)
function generateCacheOperationsHistory(): CacheOperationsDataPoint[] {
  const data: CacheOperationsDataPoint[] = [];
  const now = new Date();

  for (let i = 23; i >= 0; i--) {
    const timestamp = format(subHours(now, i), 'yyyy-MM-dd HH:mm:ss');
    const hour = subHours(now, i).getHours();
    const isBusinessHours = hour >= 9 && hour <= 17;
    const factor = isBusinessHours ? 1.5 : 1.0;

    data.push({
      timestamp,
      get_operations: randomWithVariance(550 * factor, 100),
      put_operations: randomWithVariance(85 * factor, 20),
      delete_operations: randomWithVariance(15 * factor, 5),
    });
  }

  return data;
}

// Generate top cached resources
function generateTopCachedResources(): TopCachedResource[] {
  const resources = [
    '/images/properties/featured/',
    '/images/users/avatars/',
    '/documents/contracts/',
    '/images/properties/gallery/',
    '/static/assets/icons/',
    '/images/businesses/logos/',
    '/documents/reports/',
    '/static/assets/fonts/',
    '/images/properties/thumbnails/',
    '/api/metadata/properties/',
  ];

  return resources
    .map((key, index) => ({
      key: key + `resource_${index + 1}.jpg`,
      hit_count: randomWithVariance(25000 - index * 2000, 3000),
      size_kb: randomWithVariance(450, 150),
      last_accessed: format(subHours(new Date(), randomWithVariance(2, 10)), 'yyyy-MM-dd HH:mm:ss'),
    }))
    .sort((a, b) => b.hit_count - a.hit_count);
}

// Generate database health
function generateDatabaseHealth(): AdminDatabaseHealth {
  const activeConnections = randomWithVariance(45, 10);
  const poolSize = 100;

  return {
    database_size_mb: randomWithVariance(2850, 200),
    database_size: randomWithVariance(2850, 200), // Legacy
    growth_rate_mb_per_day: parseFloat(randomWithVariance(12, 3).toFixed(2)),
    growth_rate: parseFloat(randomWithVariance(12, 3).toFixed(2)), // Legacy
    active_connections: activeConnections,
    connection_pool_size: poolSize,
    connection_pool_utilization: parseFloat(((activeConnections / poolSize) * 100).toFixed(1)),
    transaction_rate: parseFloat(randomWithVariance(125, 25).toFixed(1)),
    avg_query_time_ms: randomWithVariance(12, 3),
    slow_query_count: randomWithVariance(8, 3),
    failed_query_count: randomWithVariance(2, 1),
    query_volume: {
      select: randomWithVariance(85000, 10000),
      insert: randomWithVariance(12000, 2000),
      update: randomWithVariance(8500, 1500),
      delete: randomWithVariance(450, 100),
    },
  };
}

// Generate database growth history (30 days)
function generateDatabaseGrowthHistory(): DatabaseGrowthDataPoint[] {
  const data: DatabaseGrowthDataPoint[] = [];
  const now = new Date();
  let currentSize = 2500; // MB

  for (let i = 30; i >= 0; i--) {
    const date = format(subDays(now, i), 'yyyy-MM-dd');
    data.push({
      date,
      size_mb: parseFloat(currentSize.toFixed(2)),
    });

    currentSize += randomWithVariance(12, 3);
  }

  return data;
}

// Generate slow queries
function generateSlowQueries(): SlowQuery[] {
  return [
    {
      query: 'SELECT * FROM homes WHERE status = ? ORDER BY created_at DESC',
      avg_duration_ms: randomWithVariance(1250, 200),
      execution_count: randomWithVariance(450, 80),
      table: 'homes',
      suggestion: 'Add index on (status, created_at) for better performance',
    },
    {
      query: 'SELECT h.*, u.name FROM homes h JOIN users u ON h.owner_id = u.id WHERE u.tier = ?',
      avg_duration_ms: randomWithVariance(1850, 300),
      execution_count: randomWithVariance(280, 50),
      table: 'homes',
      suggestion: 'Consider denormalizing user tier into homes table',
    },
    {
      query: 'SELECT COUNT(*) FROM tasks WHERE home_id IN (SELECT id FROM homes WHERE owner_id = ?)',
      avg_duration_ms: randomWithVariance(2100, 350),
      execution_count: randomWithVariance(180, 40),
      table: 'tasks',
      suggestion: 'Rewrite using JOIN instead of subquery',
    },
    {
      query: 'UPDATE homes SET last_activity = ? WHERE id = ?',
      avg_duration_ms: randomWithVariance(1050, 150),
      execution_count: randomWithVariance(1200, 200),
      table: 'homes',
      suggestion: 'Consider batching updates or using triggers',
    },
    {
      query: 'SELECT * FROM messages WHERE conversation_id = ? ORDER BY created_at ASC',
      avg_duration_ms: randomWithVariance(1380, 200),
      execution_count: randomWithVariance(650, 100),
      table: 'messages',
      suggestion: 'Add index on (conversation_id, created_at)',
    },
  ].sort((a, b) => b.avg_duration_ms - a.avg_duration_ms);
}

// Generate table sizes
function generateTableSizes(): TableSize[] {
  return [
    { table_name: 'homes', size_mb: randomWithVariance(850, 100), row_count: randomWithVariance(125000, 10000) },
    { table_name: 'users', size_mb: randomWithVariance(425, 50), row_count: randomWithVariance(45000, 5000) },
    { table_name: 'tasks', size_mb: randomWithVariance(380, 40), row_count: randomWithVariance(285000, 20000) },
    { table_name: 'messages', size_mb: randomWithVariance(520, 60), row_count: randomWithVariance(450000, 30000) },
    { table_name: 'photos', size_mb: randomWithVariance(280, 30), row_count: randomWithVariance(95000, 8000) },
    { table_name: 'businesses', size_mb: randomWithVariance(145, 20), row_count: randomWithVariance(8500, 1000) },
    { table_name: 'activity_log', size_mb: randomWithVariance(185, 25), row_count: randomWithVariance(580000, 40000) },
    { table_name: 'sessions', size_mb: randomWithVariance(95, 15), row_count: randomWithVariance(12000, 2000) },
  ].sort((a, b) => b.size_mb - a.size_mb);
}

export function useAdminSystem() {
  const [isLoading, setIsLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  // Generate all system health data
  const systemHealthData = useMemo<SystemHealthData>(() => {
    const services = generateServiceStatusData();
    const errors = generateErrorData();

    return {
      services,
      serviceUptimeHistory: generateUptimeHistory(services),
      serviceHealthHeatmap: generateHealthHeatmap(services),
      errors,
      errorRateHistory: generateErrorRateHistory(),
      errorDistribution: generateErrorDistribution(errors),
      performance: generatePerformanceMetrics(),
      responseTimeHistory: generateResponseTimeHistory(),
      requestRateHistory: generateRequestRateHistory(),
      queryDurationDistribution: generateQueryDurationDistribution(),
      geographicLatency: generateGeographicLatency(),
      cacheStats: generateCacheStats(),
      cacheHitRateHistory: generateCacheHitRateHistory(),
      storageGrowthHistory: generateStorageGrowthHistory(),
      cacheOperationsHistory: generateCacheOperationsHistory(),
      topCachedResources: generateTopCachedResources(),
      databaseHealth: generateDatabaseHealth(),
      databaseGrowthHistory: generateDatabaseGrowthHistory(),
      slowQueries: generateSlowQueries(),
      tableSizes: generateTableSizes(),
      lastUpdated: format(lastRefresh, 'yyyy-MM-dd HH:mm:ss'),
    };
  }, [lastRefresh]);

  // Simulate loading when refreshing
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [lastRefresh]);

  // Manual refresh
  const refresh = () => {
    setLastRefresh(new Date());
  };

  // Clear cache action
  const clearCache = async (cacheType?: 'r2' | 'kv' | 'all') => {
    console.log(`Clearing cache: ${cacheType || 'all'}`);
    // In production, this would call API
    return { success: true };
  };

  // Resolve error action
  const resolveError = async (errorId: string) => {
    console.log(`Resolving error: ${errorId}`);
    // In production, this would call API
    // For now, just trigger refresh
    setTimeout(() => refresh(), 500);
    return { success: true };
  };

  return {
    systemHealthData,
    isLoading,
    refresh,
    clearCache,
    resolveError,
  };
}
